// GSX file decoder. Split from sandbox-gaussiansplat's converter.ts —
// keeps only the decode side (no PLY parser, no quantize, no huffman encode,
// no octree). Used by client viewer; encoder lives in the C++ Lambda binary.

import {
  BLOCK_INDEX_ENTRY_SIZE,
  FLAG_GZIP,
  GSX_VERSION,
  HEADER_SIZE,
  OCTREE_NODE_SIZE,
  type GsxBlockIndexEntry,
  type GsxHeader,
  type GsxMeta,
  type OctreeNode,
  type SplatBlock,
  readBlockIndexEntry,
  readHeader,
  readOctreeNode,
} from "./format";
import {
  dequantizeOpacity,
  dequantizePosition,
  dequantizeRotation,
  dequantizeScale,
  dequantizeShDc,
} from "./quantize";
import { huffmanDecode } from "./huffman";
import { gzipDecode } from "./codec";

const STREAM_POSITION = 0;
const STREAM_SHDC = 1;
const STREAM_SCALE = 2;
const STREAM_ROTATION = 3;
const STREAM_OPACITY = 4;

function rawLEToInt32(bytes: Uint8Array, count: number): Int32Array {
  if (bytes.byteLength < count * 4) {
    throw new Error(`raw stream truncated: ${bytes.byteLength} < ${count * 4}`);
  }
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const out = new Int32Array(count);
  for (let i = 0; i < count; i++) out[i] = view.getInt32(i * 4, true);
  return out;
}

function decodeBlockPayload(
  bytes: Uint8Array,
): { splatCount: number; streams: Map<number, { bits: number; values: Int32Array }> } {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  let p = 0;
  const splatCount = view.getUint32(p, true);
  p += 4;
  const numStreams = view.getUint8(p);
  p += 1;
  const streams = new Map<number, { bits: number; values: Int32Array }>();
  for (let i = 0; i < numStreams; i++) {
    const streamId = view.getUint8(p);
    p += 1;
    const bits = view.getUint8(p);
    p += 1;
    const mode = view.getUint8(p);
    p += 1;
    const symbolCount = view.getUint32(p, true);
    p += 4;
    if (mode === 1) {
      const tableLen = view.getUint32(p, true);
      p += 4;
      const tableBytes = bytes.subarray(p, p + tableLen);
      p += tableLen;
      const codeLen = view.getUint32(p, true);
      p += 4;
      const codeBytes = bytes.subarray(p, p + codeLen);
      p += codeLen;
      const values = huffmanDecode(codeBytes, tableBytes, symbolCount);
      streams.set(streamId, { bits, values });
    } else {
      const rawLen = view.getUint32(p, true);
      p += 4;
      const raw = bytes.subarray(p, p + rawLen);
      p += rawLen;
      streams.set(streamId, { bits, values: rawLEToInt32(raw, symbolCount) });
    }
  }
  return { splatCount, streams };
}

export function decodeGsxMeta(bytes: Uint8Array): GsxMeta {
  if (bytes.byteLength < HEADER_SIZE) throw new Error("gsx: too small for header");
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const header = readHeader(view);
  if (header.version !== GSX_VERSION) {
    throw new Error(`gsx: unsupported version ${header.version}`);
  }
  const blocks: GsxBlockIndexEntry[] = [];
  for (let i = 0; i < header.blockCount; i++) {
    blocks.push(readBlockIndexEntry(view, HEADER_SIZE + i * BLOCK_INDEX_ENTRY_SIZE));
  }
  const octreeOffset = HEADER_SIZE + header.blockCount * BLOCK_INDEX_ENTRY_SIZE;
  const nodes: OctreeNode[] = [];
  for (let i = 0; i < header.nodeCount; i++) {
    nodes.push(readOctreeNode(view, octreeOffset + i * OCTREE_NODE_SIZE));
  }
  return { header, blocks, nodes };
}

/**
 * Decode one block.
 *
 * `bytes` may be EITHER the raw range bytes (length === entry.sizeCompressed,
 * fetched per-block via Range request) OR the entire .gsx file (length larger).
 * Auto-detected by length.
 */
export async function decodeGsxBlock(
  bytes: Uint8Array,
  header: GsxHeader,
  entry: GsxBlockIndexEntry,
): Promise<SplatBlock> {
  const slice =
    bytes.byteLength === entry.sizeCompressed
      ? bytes
      : bytes.subarray(entry.offset, entry.offset + entry.sizeCompressed);
  const isGzip = (header.flags & FLAG_GZIP) !== 0;
  const payload = isGzip ? await gzipDecode(slice) : new Uint8Array(slice);
  const { splatCount, streams } = decodeBlockPayload(payload);

  const sPos = streams.get(STREAM_POSITION);
  const sSh = streams.get(STREAM_SHDC);
  const sScale = streams.get(STREAM_SCALE);
  const sRot = streams.get(STREAM_ROTATION);
  const sOpa = streams.get(STREAM_OPACITY);
  if (!sPos || !sSh || !sScale || !sRot || !sOpa) throw new Error("gsx block: missing stream");

  const positions = dequantizePosition(sPos.values, entry.aabbMin, entry.aabbMax, sPos.bits);
  const shDc = dequantizeShDc(sSh.values, sSh.bits);
  const scales = dequantizeScale(sScale.values, sScale.bits);
  const rotations = dequantizeRotation(sRot.values, sRot.bits);
  const opacities = dequantizeOpacity(sOpa.values, sOpa.bits);

  return {
    splatCount,
    positions,
    shDc,
    scales,
    rotations,
    opacities,
    aabbMin: entry.aabbMin,
    aabbMax: entry.aabbMax,
  };
}
